/* =========================================================
   Slick for Cart Drawer (Dawn-safe, non-breaking)
   - Works after Add to Cart, quantity change, remove, refresh
   - Prevents double-init
   - Waits until drawer/slider is visible (width > 0)
   ========================================================= */

/** Init slick only when:
 *  - jQuery + slick exists
 *  - slider exists
 *  - slider is visible (offsetWidth > 0)
 *  - not already slick-initialized
 */
function initFreeAdditionalProductsSlick(scope = document) {
  if (!window.jQuery || !window.jQuery.fn || typeof window.jQuery.fn.slick !== 'function') return;

  const $ = window.jQuery;
  const root = scope instanceof HTMLElement ? scope : document;
  const sliders = root.querySelectorAll('.free_additional_products');
  if (!sliders.length) return;

  sliders.forEach((el) => {
    // Don't init when hidden (drawer is closed/animating)
    if (el.offsetWidth === 0) return;

    const $el = $(el);

    // Avoid double-init
    if ($el.hasClass('slick-initialized')) return;

    try {
      $el.slick({
        dots: false,
        infinite: true,
        speed: 300,
        arrows: true,
        slidesToShow: 1,
        slidesToScroll: 1,
      });
    } catch (e) {
      // swallow errors to avoid breaking Dawn
    }
  });
}

/** Retry a few times to catch the moment the drawer becomes visible */
function initSlickWithRetry(scope, tries = 12) {
  initFreeAdditionalProductsSlick(scope);

  const root = scope instanceof HTMLElement ? scope : document;
  const hasUninit = root.querySelector('.free_additional_products:not(.slick-initialized)');

  if (hasUninit && tries > 0) {
    setTimeout(() => initSlickWithRetry(scope, tries - 1), 80);
  }
}

/** Observe cart drawer DOM changes (Dawn replaces drawer HTML) */
function observeCartDrawerForSlick() {
  const drawer = document.querySelector('cart-drawer');
  if (!drawer) return;

  // Initial attempt (in case drawer content exists already)
  initSlickWithRetry(drawer);

  const observer = new MutationObserver(() => {
    // When drawer content changes, retry init
    initSlickWithRetry(drawer);
  });

  observer.observe(drawer, { childList: true, subtree: true });
}

document.addEventListener('DOMContentLoaded', () => {
  // If slider exists anywhere on load
  initSlickWithRetry(document);

  // Always watch drawer updates
  observeCartDrawerForSlick();

  // Extra reliability: hook into Dawn cart updates (covers add-to-cart, qty/remove updates)
  try {
    if (typeof subscribe === 'function' && window.PUB_SUB_EVENTS?.cartUpdate) {
      subscribe(PUB_SUB_EVENTS.cartUpdate, () => {
        initSlickWithRetry(document.querySelector('cart-drawer') || document);
      });
    }
  } catch (e) {
    // swallow errors to avoid breaking Dawn
  }
});


/* =========================================================
   Dawn product-form.js (your original code)
   NOTE: No risky changes. Optional safe call after renderContents
   ========================================================= */

if (!customElements.get('product-form')) {
  customElements.define(
    'product-form',
    class ProductForm extends HTMLElement {
      constructor() {
        super();

        this.form = this.querySelector('form');
        this.variantIdInput.disabled = false;
        this.form.addEventListener('submit', this.onSubmitHandler.bind(this));
        this.cart = document.querySelector('cart-notification') || document.querySelector('cart-drawer');
        this.submitButton = this.querySelector('[type="submit"]');
        this.submitButtonText = this.submitButton.querySelector('span');

        if (document.querySelector('cart-drawer')) this.submitButton.setAttribute('aria-haspopup', 'dialog');

        this.hideErrors = this.dataset.hideErrors === 'true';
      }

      onSubmitHandler(evt) {
        evt.preventDefault();
        if (this.submitButton.getAttribute('aria-disabled') === 'true') return;

        this.handleErrorMessage();

        this.submitButton.setAttribute('aria-disabled', true);
        this.submitButton.classList.add('loading');
        this.querySelector('.loading__spinner').classList.remove('hidden');

        const config = fetchConfig('javascript');
        config.headers['X-Requested-With'] = 'XMLHttpRequest';
        delete config.headers['Content-Type'];

        const formData = new FormData(this.form);
        if (this.cart) {
          formData.append(
            'sections',
            this.cart.getSectionsToRender().map((section) => section.id)
          );
          formData.append('sections_url', window.location.pathname);
          this.cart.setActiveElement(document.activeElement);
        }
        config.body = formData;

        // Keep a content-type guard because Shopify can return HTML (e.g. challenge/login) instead of JSON.
        fetch(`${routes.cart_add_url}`, config)
          // .then((response) => response.json())
          .then(async (response) => {
            const contentType = response.headers.get('content-type') || '';
            if (!contentType.includes('application/json')) {
              const responseText = await response.text();
              throw new Error(
                `Expected JSON from ${routes.cart_add_url}, got ${contentType || 'unknown content type'}: ${responseText.slice(0, 120)}`
              );
            }
            return response.json();
          })
          .then((response) => {
            if (response.status) {
              publish(PUB_SUB_EVENTS.cartError, {
                source: 'product-form',
                productVariantId: formData.get('id'),
                errors: response.errors || response.description,
                message: response.message,
              });
              this.handleErrorMessage(response.description);

              const soldOutMessage = this.submitButton.querySelector('.sold-out-message');
              if (!soldOutMessage) return;
              this.submitButton.setAttribute('aria-disabled', true);
              this.submitButtonText.classList.add('hidden');
              soldOutMessage.classList.remove('hidden');
              this.error = true;
              return;
            } else if (!this.cart) {
              window.location = window.routes.cart_url;
              return;
            }

            if (!this.error)
              publish(PUB_SUB_EVENTS.cartUpdate, {
                source: 'product-form',
                productVariantId: formData.get('id'),
                cartData: response,
              });

            this.error = false;

            const quickAddModal = this.closest('quick-add-modal');
            if (quickAddModal) {
              document.body.addEventListener(
                'modalClosed',
                () => {
                  setTimeout(() => {
                    this.cart.renderContents(response);

                    // ✅ Optional safe nudge (observer + pubsub already handle this)
                    initSlickWithRetry(document.querySelector('cart-drawer') || document);
                  }, 0);
                },
                { once: true }
              );
              quickAddModal.hide(true);
            } else {
              this.cart.renderContents(response);

              // ✅ Optional safe nudge (observer + pubsub already handle this)
              initSlickWithRetry(document.querySelector('cart-drawer') || document);
            }
          })
          .catch((e) => {
            console.error(e);
          })
          .finally(() => {
            this.submitButton.classList.remove('loading');
            if (this.cart && this.cart.classList.contains('is-empty')) this.cart.classList.remove('is-empty');
            if (!this.error) this.submitButton.removeAttribute('aria-disabled');
            this.querySelector('.loading__spinner').classList.add('hidden');
          });
      }

      handleErrorMessage(errorMessage = false) {
        if (this.hideErrors) return;

        this.errorMessageWrapper =
          this.errorMessageWrapper || this.querySelector('.product-form__error-message-wrapper');
        if (!this.errorMessageWrapper) return;
        this.errorMessage = this.errorMessage || this.errorMessageWrapper.querySelector('.product-form__error-message');

        this.errorMessageWrapper.toggleAttribute('hidden', !errorMessage);

        if (errorMessage) {
          this.errorMessage.textContent = errorMessage;
        }
      }

      toggleSubmitButton(disable = true, text) {
        if (disable) {
          this.submitButton.setAttribute('disabled', 'disabled');
          if (text) this.submitButtonText.textContent = text;
        } else {
          this.submitButton.removeAttribute('disabled');
          this.submitButtonText.textContent = window.variantStrings.addToCart;
        }
      }

      get variantIdInput() {
        return this.form.querySelector('[name=id]');
      }
    }
  );
}